/**
 * THE ANCHORS A DEEP LINK CARRIES, read in one place.
 *
 * A presented notification's `deep_link` is the ONE server-built link
 * (`Inbox::DeepLink`): the desktop `?state=` blob and the mobile flat params,
 * plus, for a kind that names a destination of its own, a flat ANCHOR the
 * client reads instead of re-deriving the destination from ids it was
 * deliberately not given. The same link reaches the client two ways: in-app,
 * as the toast payload's `deep_link`; and as the URL a push tap loads the app
 * at. Both are read here, so a toast tap and a push tap can never disagree
 * about where one notice goes.
 *
 * A LEAF: it imports only the review deep-link table, the `?state=` codec and
 * the workspace's path, themselves leaves, so the inbox store reads an anchor synchronously at toast
 * time without pulling any routing or Focus module into its graph.
 */

import { WORKSPACE_PATH } from "@/constants/workspacePath";
import { reviewSourceFromDeepLink } from "@/stores/reviewSourceDeepLinks";
import { decodeState } from "@/utils/workspaceStateCodec";

/**
 * The flat query params of a root-relative link (a presented `deep_link`, or a
 * route's `fullPath`), or null when the link carries no query at all.
 *
 * @param {string} link
 * @returns {URLSearchParams|null}
 */
export function deepLinkParams(link) {
  if (typeof link !== "string") return null;
  const queryStart = link.indexOf("?");
  if (queryStart === -1) return null;
  return new URLSearchParams(link.slice(queryStart + 1).split("#")[0]);
}

// The flat anchor `Inbox::DeepLink.message_anchor_params` writes onto an inbox
// deep link. `hubId` rides alongside it and is deliberately NOT read here: the
// one opener resolves the hub itself from one coalesced topic-detail read, and
// a hubless DM topic takes its other branch.
const TOPIC_ANCHOR_PARAM = "topicId";
const MESSAGE_ANCHOR_PARAM = "messageId";

// One flat anchor id as a Number, or null. Numeric because the We topic caches
// are keyed by the server's integer id (`weTopics.hubIdByTopicId`), so a string
// id would miss the hub lookup and send a channel topic down the DM branch. The
// link builder Integer-coerces every id it encodes, so anything non-numeric
// here is a malformed link, not a different id space.
function anchorId(params, key) {
  const raw = params.get(key);
  if (raw == null || raw === "") return null;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

/**
 * The We address a deep link anchors on — `{ topicId }`, plus `messageId` when
 * the link names a message — or null when it anchors no topic.
 *
 * `messageId` is present only when the link carries one (a notice that
 * announces a condition names no message), so the opener is handed exactly the
 * address the server wrote. An Artemis ask carries one exactly when its card
 * lives in a We topic rather than on a steering thread (`Inbox::DeepLink.topic`,
 * th-uq2n5), so the inbox store's toast and the For You row both open it where
 * it lives off this one read.
 *
 * @param {string} link a presented `deep_link`
 * @returns {{ topicId: number, messageId?: number }|null}
 */
export function deepLinkAnchor(link) {
  const params = deepLinkParams(link);
  if (!params) return null;
  const topicId = anchorId(params, TOPIC_ANCHOR_PARAM);
  if (topicId == null) return null;
  const messageId = anchorId(params, MESSAGE_ANCHOR_PARAM);
  return messageId == null ? { topicId } : { topicId, messageId };
}

/**
 * The FOCUS ANCHOR's flat params: a record and the Focus section to open it at
 * (reverse-hunt-matching D15, spec hunt-match-alerts "deep-linking to the
 * section"). Mirrors `MatchAlerts::Notifications`' `ANCHOR_TYPE_PARAM` /
 * `ANCHOR_ID_PARAM` / `ANCHOR_SECTION_PARAM`, the one writer; the section is a
 * `FOCUS_SECTION_KEYS` value (`useFocusAtSection`).
 */
export const FOCUS_ANCHOR_PARAMS = Object.freeze({
  type: "focusType",
  id: "focusId",
  section: "focusSection",
});

/**
 * The record-at-a-section a link anchors on — `{ type, id, section }`, the
 * three arguments `focusAtSection` takes — or null when it anchors none.
 *
 * All three or nothing: the writer encodes them together, so a link missing
 * one is not a smaller address but a malformed one. The id is a Number, as the
 * writer Integer-coerces it; anything non-numeric addresses nothing.
 *
 * @param {string} link a presented `deep_link`, or the route's `fullPath`
 * @returns {{ type: string, id: number, section: string }|null}
 */
export function readFocusAnchor(link) {
  const params = deepLinkParams(link);
  if (!params) return null;

  const type = params.get(FOCUS_ANCHOR_PARAMS.type);
  const rawId = params.get(FOCUS_ANCHOR_PARAMS.id);
  const section = params.get(FOCUS_ANCHOR_PARAMS.section);
  if (!type || !section || rawId == null || rawId === "") return null;

  const id = Number(rawId);
  return Number.isFinite(id) ? { type, id, section } : null;
}

// The desktop form's param: base64 of the compact JSON `Inbox::DeepLink`'s one
// Me-panel encoder (`encode_me_state`) writes, which the router's
// `decompressState` reads when a link loads the app.
const STATE_PARAM = "state";

/**
 * The REVIEW SOURCE a link's desktop `?state=` blob opens — the `source` the
 * review deep-link table's own reader (`reviewSourceFromDeepLink`) answers for
 * the blob's `meTab` block — or null when the link names no review, or carries
 * a blob that is not base64 JSON.
 *
 * A review kind's address rides the blob and nowhere else: the server writes
 * its flag and filing id into `meTab` (`Inbox::DeepLink.side_quests`, artemis-scrutiny-
 * and-curiosity D26), and no flat anchor repeats them. So this reads the blob
 * through the SAME reader the Me panel host hydrates from when a link loads the
 * app (`MeTab.vue`), and a toast tap opens the source a cold link would. The
 * reader applies the table's id contracts and exclusions to a raw block, so an
 * id the router's validator would drop names nothing here either.
 *
 * @param {string} link a presented `deep_link`
 * @returns {Object|null} a `source` for the review surface
 */
export function readReviewSourceAnchor(link) {
  return reviewSourceFromDeepLink(readStateTree(link)?.meTab);
}

/**
 * The SETTINGS ANCHOR's flat param: the My Account group a server link brings
 * into view (my-account-redesign D2/D5). Mirrors `Inbox::DeepLink`'s
 * `SETTINGS_ANCHOR_PARAM`, the one writer, which encodes the page in the blob's
 * `meTab.settingsPage` and the anchor here, because the URL carries the page
 * and never the anchor.
 */
export const SETTINGS_ANCHOR_PARAM = "settingsAnchor";

/**
 * The My Account target a link anchors on — `{ page, anchor }`, the page read
 * off the blob's `meTab.settingsPage` (null when the blob names none) — or null
 * when the link carries no settings anchor.
 *
 * Both values are raw: the one opener resolves them through the roster
 * (`accountSettingsNavigation#restoreAccountSettingsAnchor`), so an anchor off
 * the roster names nothing there.
 *
 * @param {string} link a root-relative or absolute link, or the route's `fullPath`
 * @returns {{ page: string|null, anchor: string }|null}
 */
export function readSettingsAnchor(link) {
  const anchor = deepLinkParams(link)?.get(SETTINGS_ANCHOR_PARAM);
  if (!anchor) return null;

  const page = readStateTree(link)?.meTab?.settingsPage;
  return { page: typeof page === "string" ? page : null, anchor };
}

/**
 * The raw `?state=` section tree a link carries — exactly what the server
 * encoded, sections it does not name left out — or null when the link carries
 * no `state` param, or one that is not base64 JSON naming an object.
 *
 * @param {string} link a root-relative or absolute link
 * @returns {Object|null}
 */
function readStateTree(link) {
  return decodeState(deepLinkParams(link)?.get(STATE_PARAM));
}

// The paths a `?state=` tree addresses: the workspace (`WORKSPACE_PATH`), and
// `/`, which every link written before the workspace moved spells, and which
// message bodies and citations keep forever (launch-marketing-pages M1).
const STATE_LINK_PATHS = new Set(["/", WORKSPACE_PATH]);

/**
 * THE IN-APP DEEP LINK (th-lpcooq): the `?state=` tree a link opens when it
 * addresses this app's own workspace — same origin, the workspace path or the
 * `/` older links spell, a decodable `state` — or null for every other link.
 *
 * Every server-built link a message can carry (`Inbox::DeepLink` — the filing's
 * "N side quests · See them" line among them — a We message permalink, a share)
 * is the app's own workspace `?state=…` URL. Opened as a plain link it boots a second
 * Tower Hunt tab; read here, it is the tree the app applies in place through
 * the router's one `?state=` applier (`unifiedRouter.applyWorkspaceState`, the
 * load a shared link runs, minus the page load). The message link node
 * (`Link.markdoc.js`) asks this ONE question of every href, so no message
 * surface decides on its own what counts as in-app.
 *
 * @param {string} link an href, root-relative or absolute
 * @param {string} [origin] the app's origin; the page's own by default
 * @returns {Object|null} the raw section tree, or null when the link is not
 *   an in-app deep link
 */
export function readAppStateTree(link, origin = globalThis.location?.origin) {
  if (typeof link !== "string" || !origin) return null;

  let url;
  try {
    url = new URL(link, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || !STATE_LINK_PATHS.has(url.pathname)) return null;

  return readStateTree(url.search);
}
